import React, { useContext } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import firebase from "../../firebase";
import { SnackbarAlert } from "../SnackbarAlert";
import { AuthContext } from "../../auth";

import { makeStyles, withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import CancelRoundedIcon from "@material-ui/icons/CancelRounded";
import Typography from "@material-ui/core/Typography";

const useStyles = makeStyles((theme) => ({
  input: {
    margin: 8,
  },
  button: {
    color: theme.palette.primary.main,
    textTransform: "none",
    padding: theme.spacing(1),
    fontSize: "1.3em",
    fontWeight: 600,
    letterSpacing: 1,
  },
  dialog: {
    padding: theme.spacing(1),
    width: "auto",
    color: theme.palette.common.white,
  },
  dialogTitlePost: {
    background: "linear-gradient(to right bottom, #03ceac, #03bc9b)",
  },
  dialogActionsPost: {
    background: "hsl(0, 0%, 97%)",
  },
}));

const styles = (theme) => ({
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[50],
    fontSize: 40,
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, action, className, classes, ...other } = props;
  return (
    <MuiDialogTitle disableTypography {...other} className={className}>
      <Typography component="div">
        <Box
          fontSize={24}
          fontWeight="fontWeightBold"
          letterSpacing={0.7}
          p={1}
        >
          {children}
        </Box>
      </Typography>
      {action ? (
        <span>
          <Tooltip
            title="Close"
            enterDelay={300}
            leaveDelay={200}
            enterNextDelay={300}
          >
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={action}
            >
              <CancelRoundedIcon />
            </IconButton>
          </Tooltip>
        </span>
      ) : null}
    </MuiDialogTitle>
  );
});

const EditPostForm = (props) => {
  const classes = useStyles();
  const { currentUser } = useContext(AuthContext);
  const { setSnackbarState } = useContext(SnackbarAlert);

  return (
    <Formik
      initialValues={{
        inputTitle: props.postsTitle,
        inputMessage: props.message,
        hideName: false,
      }}
      validationSchema={Yup.object().shape({
        inputTitle: Yup.string().required("Please enter your query"),
        inputMessage: Yup.string().required("Please explain your query"),
        hideName: Yup.boolean(),
      })}
      onSubmit={async (values, actions) => {
        try {
          let displayName = "";
          if (values.hideName === true) {
            displayName = "Anonymous User";
          } else {
            displayName = currentUser.displayName;
          }
          const postData = {
            fromPost: props.fromPost,
            message: values.inputMessage,
            postsDate: props.postsTimestamp,
            postsEdited: true,
            postsName: props.postsName,
            postsTitle: values.inputTitle,
            postsUserID: props.postsUserID,
            voters: props.voters,
            votes: props.votes,
          };
          let updates = {};
          updates[`posts/${props.urlParam}/${props.id}`] = postData;
          await firebase.database().ref().update(updates);
          setTimeout(() => {
            actions.resetForm();
            props.close();
            setSnackbarState({
              state: true,
              message: "Successfully updated! 👍",
              status: "info",
            });
          }, 0);
        } catch (error) {
          actions.resetForm();
          props.close();
          //console.log(error);
          setSnackbarState({
            state: true,
            message: "Something went wrong. 😟",
            status: "error",
          });
        }
      }}
    >
      {(formik) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
          key={props.id}
          noValidate
        >
          <DialogTitle
            id="form-dialog-title"
            action={props.close}
            className={clsx(classes.dialog, classes.dialogTitlePost)}
          >
            Edit Post
          </DialogTitle>
          <DialogContent>
            <Grid
              container
              direction="column"
              justify="flex-start"
              alignItems="stretch"
            >
              <Grid item xs={12} zeroMinWidth>
                <TextField
                  id="inputTitle"
                  name="inputTitle"
                  placeholder="Ask your questions or tell us your recommendations, queries and concerns."
                  margin="normal"
                  variant="outlined"
                  className={classes.input}
                  disabled={formik.isSubmitting}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  {...formik.getFieldProps("inputTitle")}
                  error={formik.errors.inputTitle && formik.touched.inputTitle}
                  helperText={
                    formik.errors.inputTitle && formik.touched.inputTitle
                      ? formik.errors.inputTitle
                      : "Title"
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} zeroMinWidth>
                <TextField
                  id="inputMessage"
                  name="inputMessage"
                  placeholder="Explain your recommendation, query, or concern."
                  margin="normal"
                  variant="outlined"
                  className={classes.input}
                  disabled={formik.isSubmitting}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  {...formik.getFieldProps("inputMessage")}
                  error={
                    formik.errors.inputMessage && formik.touched.inputMessage
                  }
                  helperText={
                    formik.errors.inputMessage && formik.touched.inputMessage
                      ? formik.errors.inputMessage
                      : "Message"
                  }
                  rows={4}
                  multiline
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} zeroMinWidth>
                <Box component="div" display="inline" justify="flex-end" m={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="hideName"
                        name="hideName"
                        type="checkbox"
                        checked={formik.values.hideName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        color="secondary"
                      />
                    }
                    label="Post as Anonymous User"
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            className={clsx(classes.dialog, classes.dialogActionsPost)}
          >
            <Button
              variant="text"
              type="submit"
              className={classes.button}
              disabled={formik.isSubmitting}
              fullWidth
            >
              Save Changes
            </Button>
          </DialogActions>
        </form>
      )}
    </Formik>
  );
};

export default EditPostForm;
